import _ from 'underscore';

import CardReaderDelegate from 'app/retail_manager/CardReaderDelegate';
import BaseObject from 'app/src/base/BaseObject';
import SalePayment from 'app/src/data/models/SalePayment';
import Filter from 'app/src/data/models/utils/Filter';
import PaymentFactory from 'app/src/payments/PaymentFactory';
import PaymentTypeManager from 'app/src/payments/PaymentTypeManager';
import SwipePayment from 'app/src/payments/SwipePayment';

let _eventGenerator = new BaseObject();
let _depositAmount = null;

// export all the generated events under the Retail Manager for convenience
let _Events = {};
_.extend(_Events, CardReaderDelegate.Event);

export default class RetailManager {
  /**
   * Retail Manager singleton constructor.
   *
   * @returns {RetailManager}    The singleton instance of the RetailManager
   */
  static init() {
    // Since we have no need to subsume all Stencil integration at this juncture,
    // we'll just provide our own concrete implementation and delegations for the
    // behaviour that we require and leave all others as they are.
    if (RetailManager.active) {
      RetailManager.cardReader.setProcessingDelegate(new CardReaderDelegate());

      RetailManager.events.listenTo(
        RetailManager.cardReader._processingDelegate.eventGenerator,
        'all',
        RetailManager._onCardReaderEvent
      );
    }
  }

  /**
   * Export the events that the Retail Manager triggers via the class namespace.
   *
   * @returns {CardReaderDelegate.Event}    The events
   */
  static get Event() {
    return _Events;
  }

  /**
   * The Retail Manager is generally accessed via static methods since it is,
   * for the most part, an adapter for the Stencil Web API.  In order to take advantage
   * of event generation, however, we are required to create an instance of an object so we've
   * implemented a simple event generator to which interested parties may listen to.
   */
  static get events() {
    return _eventGenerator;
  }

  /**
   * Retrieves the object used to before interactions with a Stencil-configured
   * card reader/terminal.
   *
   * @returns {CardReader|null}    The card reader StencilWebAPI object
   */
  static get cardReader() {
    return window.StencilWebAPI.cardReader;
  }

  /**
   * When true, Retail Manager is active.  That is to say that the Stencil Web API
   * has detected that it is running within Stencil.
   *
   * @returns {boolean}   True when the Retail Manager is active
   */
  static get active() {
    return window.StencilWebAPI.isStencil();
  }

  static get isAndroid() {
    return window.StencilWebAPI.isAndroid();
  }
  /**
   * Event handler for Card Reader events
   *
   * @param {Event} event The triggered event
   * @param {object|string} data The data passed to the StencilWebAPI.  Generally
   *                             this would either be null or contain a payment ID.
   *                             For notifications for terminals that have no back-end
   *                             integration with the payment provider (e.g. iZettle) additional
   *                             data used to create a payment would be provided.
   * @private
   */
  static _onCardReaderEvent(event, data = null) {
    switch (event) {
      case CardReaderDelegate.Event.PAYMENT_COMPLETE:
        let payment = null;
        if (data && data.paymentTypeID) {
          // for payments where a paymentTypeID is present, create a payment
          // that we will pass along with the notification
          payment = PaymentFactory.createPayment(
            PaymentTypeManager.Types.PENDING,
            data.amount,
            data.paymentTypeID
          );
          payment.transactionReferenceID = data.transactionReferenceID;
        }

        if (_depositAmount) {
          // If the payment was for a deposit, then we need to create the
          // deposit payment to be added to the sale.  We don't have access
          // to a credit account ID so that will have to be taken care of
          // by the listener to the PAYMENT_COMPLETE event.
          _depositAmount = null;

          if (!payment) {
            // We need to fetch the sale payment that was created by Stencil
            // in order to create the deposit payment
            let salePayment = new SalePayment({
              salePaymentID: parseInt(data.salePaymentID || 0),
            });
            let filter = new Filter().loadRelations('all');

            salePayment.fetch(filter).done(() => {
              payment = PaymentFactory.createPayment(
                PaymentTypeManager.Types.DEPOSIT,
                PaymentFactory.createPaymentFromModel(salePayment)
              );

              RetailManager.events.trigger(_Events.PAYMENT_COMPLETE, payment);
            });
          } else {
            payment = PaymentFactory.createPayment(PaymentTypeManager.Types.DEPOSIT, payment);
            RetailManager.events.trigger(_Events.PAYMENT_COMPLETE, payment);
          }
        } else {
          RetailManager.events.trigger(_Events.PAYMENT_COMPLETE, payment);
        }

        break;

      default:
        RetailManager.events.trigger(event, data, _depositAmount);

        // be sure to clear the deposit flag for any event
        _depositAmount = null;
        break;
    }
  }

  /**
   * Given a collection of sale payments, this method collects only the swipe payments from the
   * collecton and composes them into a format consumable by the Stencil Web API.
   *
   * @param {SalePaymentCollection} creditDebitPayments   A collection of credit/debit payments
   */
  static sendSignatureData(creditDebitPayments) {
    if (creditDebitPayments) {
      let swipePayments = [];

      creditDebitPayments.forEach((creditDebitPayment) => {
        // find any swipe payments...
        let payment = PaymentFactory.createPaymentFromModel(creditDebitPayment);

        if (payment instanceof SwipePayment) {
          // format the swipe data in the format the Stencil Web API expects
          swipePayments.push({
            transaction_payment_id: payment.model.id.toString(),
            cc_charge_id: {
              card_type: payment.cardType,
              xnum: payment.maskedDigits,
            },
            amount: payment.amount.toString(),
          });
        }
      });

      if (swipePayments.length && RetailManager.active) {
        // only request signature capture if we have swiped payments
        window.StencilWebAPI.signatureCapture.sendSignatureData(swipePayments);
      }
    } else {
      console.error('A sale manager must be specified when sending signature data.');
    }
  }

  /**
   * Initiates a deposit transaction.
   *
   * @param {number} saleID   The ID of the sale
   * @param {Decimal} amount  The amount to be transacted
   */
  static initiateDepositTransaction(saleID, amount) {
    _depositAmount = amount;
    RetailManager.cardReader.initiatePaymentTerminalTransaction(
      saleID.toString(),
      Number(amount.toString())
    );
  }
}
